import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { exercisesApi, postsApi, platformsApi, mediaApi } from '../../lib/mpApi';
import { SocialPostTemplate, isNewsPlatform, NewsTemplate } from '../../components/mp/SocialPostTemplates';
import NewsArticleDetailDialog from '../../components/mp/NewsArticleDetailDialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Radio, Sun, Moon, RefreshCw, ArrowLeft, Maximize2, Minimize2,
  Plus, Loader2, FileUp, X, Film, Send, Zap,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Inline comments list component
function InlineComments({ postId, isDark, refreshKey }) {
  const [comments, setComments] = useState([]);

  useEffect(() => {
    if (!postId) return;
    fetch(`${BACKEND_URL}/api/mp/guest/comments/${postId}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setComments(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [postId, refreshKey]);

  if (!comments.length) return null;

  const formatTime = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  };

  return (
    <div className={`mt-1 rounded-lg border overflow-hidden ${isDark ? 'bg-card/50 border-border' : 'bg-gray-50 border-border'}`}>
      <div className="max-h-32 overflow-y-auto divide-y divide-border/30">
        {comments.slice(-5).map(c => (
          <div key={c.comment_id} className="px-2.5 py-1.5 flex items-start gap-2">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ${c.author_type === 'guest' ? 'bg-orange-500/20 text-orange-600' : 'bg-blue-500/20 text-blue-600'}`}>
              {c.author_name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={`text-[11px] font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.author_name}</span>
                {c.author_type === 'guest' && <span className="text-[9px] px-1 py-px rounded bg-orange-500/10 text-orange-600">Guest</span>}
                <span className={`text-[9px] ml-auto ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>{formatTime(c.created_at)}</span>
              </div>
              <p className={`text-[11px] leading-tight ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{c.content}</p>
            </div>
          </div>
        ))}
      </div>
      {comments.length > 5 && (
        <div className={`text-center text-[10px] py-1 ${isDark ? 'text-zinc-500 bg-card/80' : 'text-gray-400 bg-gray-100'}`}>
          +{comments.length - 5} more comments
        </div>
      )}
    </div>
  );
}
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;

const MPPublicDisplayPage = () => {
  const { user, token } = useAuth();
  // V14.10.3 — participants treat /hub as their home; everyone else returns to MP dashboard.
  const isParticipantUser = (user?.roles || []).includes('participant')
    && !(user?.roles || []).some(r => ['super_admin', 'hub_admin', 'simcel', 'observer'].includes(r));
  const [exercises, setExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [posts, setPosts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPostAnim, setNewPostAnim] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const [detailPost, setDetailPost] = useState(null);
  // V14.11.2 — Honor `?exercise=<id>` query param so Live Display opened
  // from a specific MP Exercise Dashboard / Live Preview stays on that
  // exercise instead of defaulting to the first active one.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedExerciseId = searchParams.get('exercise');

  const [createForm, setCreateForm] = useState({
    platform: '', content: '', title: '', post_type: 'social_media',
    author: { name: user?.name || '', handle: '', bio: '', followers: 0, is_verified: false },
  });

  const platformLookup = {};
  platforms.forEach(p => { platformLookup[p.name.toLowerCase()] = p; });

  const loadExercises = useCallback(async () => {
    try {
      const data = await exercisesApi.getAll();
      const active = (Array.isArray(data) ? data : []).filter(e => e.status === 'enabled' || e.status === 'published');
      setExercises(active);
      if (active.length > 0 && !selectedExercise) {
        // Prefer ?exercise=<id> from URL; otherwise fall back to first active.
        const requested = requestedExerciseId
          ? active.find(e => e.exercise_id === requestedExerciseId)
          : null;
        setSelectedExercise(requested || active[0]);
      }
      // V14.10 — when no exercises are accessible (typical for fresh
      // participants who haven't been assigned yet), drop the spinner so the
      // empty-state UI renders instead of an infinite loader.
      if (active.length === 0) setLoading(false);
    } catch {
      toast.error('Failed to load exercises');
      setLoading(false);
    }
  }, [selectedExercise, requestedExerciseId]);

  const loadPosts = useCallback(async () => {
    if (!selectedExercise) return;
    try {
      const [postsData, platData] = await Promise.all([
        postsApi.getAll({ exercise_id: selectedExercise.exercise_id }),
        platformsApi.getAll().catch(() => []),
      ]);
      setPosts(Array.isArray(postsData) ? postsData.filter(p => p.status === 'published') : []);
      setPlatforms(Array.isArray(platData) ? platData : []);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, [selectedExercise]);

  useEffect(() => { loadExercises(); }, [loadExercises]);
  useEffect(() => { if (selectedExercise) { setLoading(true); loadPosts(); } }, [selectedExercise, loadPosts]);

  // Polling fallback for like/comment sync (every 15s)
  useEffect(() => {
    if (!selectedExercise) return;
    const interval = setInterval(() => { loadPosts(); }, 15000);
    return () => clearInterval(interval);
  }, [selectedExercise, loadPosts]);

  // Socket.IO for live updates — uses WebSocketContext
  useEffect(() => {
    if (!selectedExercise?.exercise_id) return;

    // Listen for MP events via window CustomEvents dispatched by WebSocketContext
    const handleWsMessage = (e) => {
      const msg = e.detail;
      if (msg.type === 'new_post' && msg.data) {
        setPosts(prev => [msg.data, ...prev]);
        setNewPostAnim(msg.data.post_id);
        setTimeout(() => setNewPostAnim(null), 3000);
        toast.info('New post published');
      } else if (msg.type === 'post_published' && msg.data) {
        setPosts(prev => [msg.data, ...prev.filter(p => p.post_id !== msg.data.post_id)]);
        setNewPostAnim(msg.data.post_id);
        setTimeout(() => setNewPostAnim(null), 3000);
        toast.info('Post published');
      } else if (msg.type === 'new_comment' || msg.type === 'new_reaction' || msg.type === 'post_updated') {
        loadPosts();
      }
    };

    window.addEventListener('ws-message', handleWsMessage);

    // Join the MP exercise room via Socket.IO
    const token = localStorage.getItem('ems_token');
    if (token) {
      // Dynamically import socket.io-client for this page
      import('socket.io-client').then(({ io }) => {
        const socket = io(BACKEND_URL, {
          path: '/api/socket.io',
          auth: { token },
          reconnection: true,
          reconnectionDelay: 2000,
          transports: ['websocket', 'polling'],
        });
        socket.on('connect', () => {
          socket.emit('join_mp_exercise', { exercise_id: selectedExercise.exercise_id });
        });
        socket.on('new_post', (data) => {
          if (data?.data) {
            setPosts(prev => [data.data, ...prev]);
            setNewPostAnim(data.data.post_id);
            setTimeout(() => setNewPostAnim(null), 3000);
            toast.info('New post published');
          }
        });
        socket.on('post_published', (data) => {
          if (data?.data) {
            setPosts(prev => [data.data, ...prev.filter(p => p.post_id !== data.data.post_id)]);
            setNewPostAnim(data.data.post_id);
            setTimeout(() => setNewPostAnim(null), 3000);
            toast.info('Post published');
          }
        });
        socket.on('new_comment', () => loadPosts());
        socket.on('new_reaction', () => loadPosts());
        socket.on('post_updated', () => loadPosts());
        wsRef.current = socket;
      });
    }

    return () => {
      window.removeEventListener('ws-message', handleWsMessage);
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
      }
    };
  }, [selectedExercise, loadPosts]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Active comment state: { postId, text }
  const [activeComment, setActiveComment] = useState(null);

  // Handle post interaction (like, comment, share, repost, retweet)
  const handlePostInteract = useCallback(async (postId, type) => {
    if (type === 'comment') {
      // Toggle inline comment input instead of window.prompt
      setActiveComment(prev => prev?.postId === postId ? null : { postId, text: '' });
      return;
    }
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      let res;
      if (type === 'like') {
        res = await fetch(`${BACKEND_URL}/api/mp/guest/reactions`, {
          method: 'POST', headers,
          body: JSON.stringify({ post_id: postId, reaction_type: 'like' }),
        });
      } else if (type === 'share' || type === 'repost' || type === 'retweet') {
        res = await fetch(`${BACKEND_URL}/api/mp/posts/${postId}`, {
          method: 'PUT', headers,
          body: JSON.stringify({ engagement: { shares: 1 } }),
        });
      }
      if (res && !res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} recorded`);
      loadPosts();
    } catch {
      toast.error(`Failed to ${type}`);
    }
  }, [token, loadPosts]);

  // Submit inline comment
  const submitComment = useCallback(async () => {
    if (!activeComment?.postId || !activeComment?.text?.trim()) return;
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const res = await fetch(`${BACKEND_URL}/api/mp/guest/comments`, {
        method: 'POST', headers,
        body: JSON.stringify({ post_id: activeComment.postId, content: activeComment.text.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Comment added');
      setActiveComment(null);
      loadPosts();
    } catch {
      toast.error('Failed to comment');
    }
  }, [activeComment, token, loadPosts]);

  const handleQuickCreate = async () => {
    if (!createForm.platform || !createForm.content) { toast.error('Platform and content required'); return; }
    setCreating(true);
    try {
      let mediaUrls = [];
      for (const file of selectedFiles) {
        try {
          const result = await mediaApi.upload(file);
          mediaUrls.push(result.file_url);
        } catch { /* noop */ }
      }
      const payload = {
        exercise_id: selectedExercise.exercise_id,
        platform: createForm.platform,
        content: createForm.content,
        title: createForm.title,
        post_type: createForm.post_type,
        author: createForm.author,
        engagement: { likes: 0, comments: 0, shares: 0, views: 0 },
        status: 'published',
        media_urls: mediaUrls,
        hashtags: [],
      };
      await postsApi.create(payload);
      toast.success('Post published to live display');
      setShowCreateDialog(false);
      setSelectedFiles([]);
      setCreateForm({
        platform: '', content: '', title: '', post_type: 'social_media',
        author: { name: user?.name || '', handle: '', bio: '', followers: 0, is_verified: false },
      });
      loadPosts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const displayPlatforms = selectedExercise?.selected_platforms?.length > 0
    ? selectedExercise.selected_platforms
    : [...new Set(posts.map(p => p.platform))];

  const getPostsByPlatform = (name) => posts.filter(p => p.platform?.toLowerCase() === name?.toLowerCase());
  const getWidthForPlatform = (name) => {
    const c = selectedExercise?.platform_widths?.find(pw => pw.platform === name);
    return c ? c.width : Math.floor(100 / Math.max(displayPlatforms.length, 1));
  };
  const getPlatformDisplayName = (name) => platformLookup[name?.toLowerCase()]?.display_name || name;
  const getPlatformColor = (name) => platformLookup[name?.toLowerCase()]?.color || '#666';
  const getPlatformIcon = (name) => {
    const p = platformLookup[name?.toLowerCase()];
    if (p?.custom_icon_url) return <img src={resolveUrl(p.custom_icon_url)} alt="" className="w-full h-full object-contain" />;
    return <span className="text-xs font-bold">{(p?.display_name || name)?.charAt(0).toUpperCase()}</span>;
  };

  const newsPlatformColors = { bbc: '#BB1919', cnn: '#CC0000', reuters: '#FF8000', aljazeera: '#D4AA00', nytimes: '#000', guardian: '#052962', foxnews: '#003366', rt: '#5EAA22', xinhua: '#D52B1E' };

  const socialPlatforms = platforms.filter(p => !isNewsPlatform(p.name));
  const newsPlatformsList = platforms.filter(p => isNewsPlatform(p.name) || p.category === 'news');

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-background text-foreground' : 'bg-muted text-foreground'}`} data-testid="mp-public-display">
      {/* Header */}
      <header className={`sticky top-0 z-40 border-b backdrop-blur ${isDark ? 'border-border bg-background/95' : 'border-border bg-white/95'}`}>
        <div className="flex h-12 items-center gap-3 px-4">
          {/* V14.10.3 — Back goes to /hub for participants (Hub is their home),
              otherwise to the MP dashboard. */}
          <Link
            to={isParticipantUser ? '/hub' : '/mp/dashboard'}
            data-testid="mp-public-display-back"
            className={`flex items-center gap-1.5 text-xs transition ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <div className={`w-px h-6 ${isDark ? 'bg-card' : 'bg-muted'}`} />
          {selectedExercise?.logo_url ? (
            <img src={selectedExercise.logo_url.startsWith('/') ? `${BACKEND_URL}${selectedExercise.logo_url}` : selectedExercise.logo_url}
              alt="" className="w-7 h-7 rounded object-contain bg-white border border-border flex-shrink-0" />
          ) : (
            <Radio className="w-4 h-4 text-red-500" />
          )}
          <h1 className="font-heading font-bold text-sm tracking-tight uppercase">{selectedExercise?.name || 'Live Display'}</h1>
          <Badge className="bg-red-500/10 text-red-500 border-red-500/30 text-[10px] font-bold uppercase animate-pulse">Live</Badge>

          {selectedExercise && (
            <div className="hidden sm:flex items-center gap-1 text-[10px] text-muted-foreground">
              <span>{posts.length} posts</span>
              <span>&middot;</span>
              <span>{displayPlatforms.length} platforms</span>
            </div>
          )}

          <div className="flex-1" />

          {/* Quick Create */}
          {selectedExercise && (
            <Button size="sm" onClick={() => setShowCreateDialog(true)}
              className="bg-red-600 hover:bg-red-700 text-foreground h-8 text-xs" data-testid="mp-live-create-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New Post
            </Button>
          )}

          <Select value={selectedExercise?.exercise_id || ''} onValueChange={v => {
            const ex = exercises.find(e => e.exercise_id === v);
            setSelectedExercise(ex);
            // Keep URL in sync so refresh / share preserves selection.
            if (ex) setSearchParams({ exercise: ex.exercise_id }, { replace: true });
          }}>
            <SelectTrigger className={`w-44 h-8 text-xs ${isDark ? 'bg-card border-input' : 'bg-white border-border'}`}><SelectValue placeholder="Select exercise" /></SelectTrigger>
            <SelectContent>{exercises.map(e => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>

          <Button variant="ghost" size="icon" onClick={loadPosts} className={`h-8 w-8 ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-foreground'}`}><RefreshCw className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" onClick={toggleFullscreen} className={`h-8 w-8 ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}</Button>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className={`h-8 w-8 ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </header>

      {/* Platform Columns */}
      {loading ? (
        <div className="flex items-center justify-center h-[calc(100vh-3rem)]"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : !selectedExercise ? (
        <div className="flex flex-col items-center justify-center h-[calc(100vh-3rem)] gap-3 px-6 text-center"
          data-testid="mp-display-empty-state">
          <Radio className="w-12 h-12 text-muted-foreground/50" />
          <h2 className="font-heading text-lg uppercase tracking-tight">No exercises available</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            You are not assigned to any active Media Portal exercise yet. Please ask
            your exercise controller to assign you to a published/enabled exercise.
          </p>
          <p className="text-xs text-muted-foreground/70 mt-2">
            Logged in as <span className="font-medium">{user?.name || user?.email}</span>
          </p>
        </div>
      ) : (
        <div className="flex h-[calc(100vh-3rem)] overflow-x-auto overflow-y-hidden mp-platform-strip">
          {displayPlatforms.map(platformName => {
            const width = getWidthForPlatform(platformName);
            const platformPosts = getPostsByPlatform(platformName);
            const plat = platforms.find(p => p.name === platformName);
            // V14.13.11.3 — Category-aware detection.
            const platIsNews = plat?.category === 'news' || isNewsPlatform(platformName);
            const color = getPlatformColor(platformName);
            const dName = getPlatformDisplayName(platformName);
            return (
              <div key={platformName} className={`border-r ${isDark ? 'border-border' : 'border-border'} flex-shrink-0 overflow-hidden`}
                style={{ width: `${width}%`, minWidth: '280px' }}>
                <ScrollArea className="h-full">
                  <div className="p-3 space-y-3">
                    <div className={`flex items-center gap-2 pb-2 border-b sticky top-0 z-10 ${isDark ? 'border-border bg-background/95 backdrop-blur-sm' : 'border-border bg-white/95 backdrop-blur-sm'}`}>
                      <div className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: color }}>{getPlatformIcon(platformName)}</div>
                      <h2 className="font-heading font-bold text-xs uppercase tracking-wide">{dName}</h2>
                      <Badge variant="secondary" className={`ml-auto text-[10px] ${isDark ? 'bg-muted text-muted-foreground' : 'bg-muted text-muted-foreground'}`}>{platformPosts.length}</Badge>
                    </div>
                    {platformPosts.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground"><p className="text-xs">No posts yet</p></div>
                    ) : (
                      <div className="space-y-3">
                        {platformPosts.map(post => {
                          const isNews = platIsNews || post.post_type === 'news';
                          return (
                          <div key={post.post_id}>
                            <div className={`transition-all duration-500 ${newPostAnim === post.post_id ? `ring-2 ring-red-500 ring-offset-2 ${isDark ? 'ring-offset-gray-950' : 'ring-offset-white'} rounded-lg` : ''}`}>
                              {isNews
                                ? <div data-testid={`mp-public-news-${post.post_id}`}>
                                    <NewsTemplate post={post} platformColor={newsPlatformColors[post.platform?.toLowerCase()] || color} onOpen={() => setDetailPost(post)} />
                                  </div>
                                : <SocialPostTemplate post={post} platform={post.platform} onInteract={handlePostInteract} onOpen={() => setDetailPost(post)} />}
                            </div>
                            {/* Inline comment input */}
                            {activeComment?.postId === post.post_id && (
                              <div className={`mt-1 flex gap-1.5 p-2 rounded-lg border ${isDark ? 'bg-card border-border' : 'bg-white border-border'}`}>
                                <Input
                                  value={activeComment.text}
                                  onChange={e => setActiveComment(prev => ({ ...prev, text: e.target.value }))}
                                  placeholder="Write a comment..."
                                  className="h-8 text-xs flex-1"
                                  autoFocus
                                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                                  data-testid={`comment-input-${post.post_id}`}
                                />
                                <Button size="sm" className="h-8 px-2.5 bg-blue-600 hover:bg-blue-700 text-white" onClick={submitComment} data-testid={`comment-submit-${post.post_id}`}>
                                  <Send className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                            {/* Inline comments list */}
                            <InlineComments postId={post.post_id} isDark={isDark} refreshKey={posts} />
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
          {displayPlatforms.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Radio className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No platforms configured</p>
                <p className="text-xs mt-1">Select an active exercise to see the live feed</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg ">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-foreground flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" /> Quick Publish
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Platform</Label>
                <Select value={createForm.platform} onValueChange={v => {
                  const isNewsPlat = isNewsPlatform(v);
                  setCreateForm({...createForm, platform: v, post_type: isNewsPlat ? 'news' : 'social_media'});
                }}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {displayPlatforms.map(pName => {
                      const plat = platformLookup[pName?.toLowerCase()];
                      return <SelectItem key={pName} value={pName}>{plat?.display_name || pName}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
                <Select value={createForm.post_type} onValueChange={v => setCreateForm({...createForm, post_type: v})}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="social_media">Social Post</SelectItem>
                    <SelectItem value="news">News Article</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {createForm.post_type === 'news' && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Headline</Label>
                <Input value={createForm.title} onChange={e => setCreateForm({...createForm, title: e.target.value})}
                  placeholder="Breaking headline..." className="bg-muted border-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Content</Label>
              <Textarea value={createForm.content} onChange={e => setCreateForm({...createForm, content: e.target.value})}
                placeholder="Write your post..." rows={4} className="bg-muted border-input placeholder:text-muted-foreground" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Author Name</Label>
                <Input value={createForm.author.name} onChange={e => setCreateForm({...createForm, author: {...createForm.author, name: e.target.value}})}
                  className="bg-muted border-input h-8 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Handle</Label>
                <Input value={createForm.author.handle} onChange={e => setCreateForm({...createForm, author: {...createForm.author, handle: e.target.value}})}
                  placeholder="@handle" className="bg-muted border-input h-8 text-sm" />
              </div>
            </div>
            {/* Media */}
            <div className="space-y-2">
              <div className="border-2 border-dashed border-input rounded-lg p-3 text-center bg-muted">
                <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={e => setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)])} className="hidden" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                  className="border-border text-foreground/80 hover:bg-muted h-7 text-xs">
                  <FileUp className="w-3 h-3 mr-1" />Add Media
                </Button>
              </div>
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedFiles.map((file, i) => (
                    <div key={i} className="relative group/img w-12 h-12 border border-input rounded overflow-hidden bg-muted flex items-center justify-center">
                      {file.type.startsWith('video/') ? <Film className="w-4 h-4 text-muted-foreground" /> : <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />}
                      <button onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-0 right-0 w-3 h-3 bg-red-500 text-foreground rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100"><X className="w-2 h-2" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="border-t border-border pt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-input text-muted-foreground hover:bg-muted">Cancel</Button>
            <Button onClick={handleQuickCreate} disabled={creating} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-live-publish-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Publish Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NewsArticleDetailDialog open={!!detailPost} onOpenChange={(o) => !o && setDetailPost(null)} post={detailPost} />
    </div>
  );
};

export default MPPublicDisplayPage;
